import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Flame } from "lucide-react";

const HEAT = {
  hot: { color: "#E11D48", label: "Hot" },
  warm: { color: "#F59E0B", label: "Warm" },
  cool: { color: "#0EA5E9", label: "Cool" },
  cold: { color: "#64748B", label: "Cold" },
};
const ORDER = ["hot", "warm", "cool", "cold"];

export default function HeatMap() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/insights/win-heatmap").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="px-6 sm:px-8 py-8" data-testid="heatmap-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
          <Flame className="w-5 h-5 text-[#E11D48]" /> Win heat map
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Every live pursuit ranked hottest to coldest by likelihood to win — blending price competitiveness, engagement momentum and how responsive the client is. Start at the top.</p>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : data.count === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="heatmap-empty">
          No live pursuits to rank yet — bids in estimating through submitted will appear here.
        </Card>
      ) : (
        <>
          <Card className="p-6 mb-6 flex flex-wrap items-center gap-8" data-testid="heatmap-summary">
            <div>
              <div className="font-display text-4xl font-bold text-black tabular-nums" data-testid="heatmap-avg">{data.avg_score}</div>
              <div className="text-xs text-slate-500 mt-1">Average heat — {data.count} live pursuits</div>
            </div>
            <div className="flex flex-wrap gap-4">
              {ORDER.map((h) => (
                <div key={h} className="text-center" data-testid={`heatmap-band-${h}`}>
                  <div className="font-display text-2xl font-bold tabular-nums" style={{ color: HEAT[h].color }}>{data.heat_counts[h]}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 justify-center">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: HEAT[h].color }} /> {HEAT[h].label}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="space-y-2.5">
            {data.pursuits.map((p, i) => {
              const h = HEAT[p.heat] || HEAT.cold;
              return (
                <button key={p.bid_id} onClick={() => nav(`/bids/${p.bid_id}?tab=context`)}
                  data-testid={`heatmap-row-${p.bid_id}`}
                  className="w-full text-left rounded-xl border border-slate-200 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all flex overflow-hidden">
                  <span className="w-1.5 shrink-0" style={{ backgroundColor: h.color }} />
                  <div className="flex-1 min-w-0 p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-bold text-slate-300 tabular-nums w-6 shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-black truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 truncate">{p.client || "No client"}{p.mw ? ` · ${p.mw} MW` : ""} · {p.status}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display text-2xl font-bold tabular-nums" style={{ color: h.color }}>{p.score}</div>
                        <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: h.color }}>{h.label}</div>
                      </div>
                    </div>
                    <div className="mt-2.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${p.score}%`, backgroundColor: h.color }} />
                    </div>
                    {p.reasons?.length ? (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {p.reasons.map((r, idx) => (
                          <span key={idx} className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{r}</span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
